import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Calendar, Shield, Edit2, Save, X, Camera, MapPin, Briefcase, Award, Star, Upload, Users } from 'lucide-react';
import { updateProfile, updateEmail, updatePassword } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, query, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { USER_ROLES, ROLE_INFO, getDefaultRole, getRoleBadgeProps } from '../utils/roles';
import { AVATARS, getDefaultAvatar } from '../utils/avatars';
import FollowButton from './FollowButton';

export default function UserProfile({ user, onClose, onAvatarUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [editForm, setEditForm] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    bio: '',
    location: '',
    traderType: '',
    tradingExperience: '',
    favoriteStocks: '',
    avatar: getDefaultAvatar()
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    loadUserData();
    loadFollowCounts();
  }, [user]);

  const loadFollowCounts = async () => {
    if (!user) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${user.uid}/follow-counts`);
      const data = await response.json();

      if (data.success) {
        setFollowCounts({
          followers: data.followers,
          following: data.following
        });
      }
    } catch (error) {
      console.error('Error loading follow counts:', error);
    }
  };

  const loadUserData = async () => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        setEditForm({
          displayName: user.displayName || '',
          email: user.email || '',
          bio: data.bio || '',
          location: data.location || '',
          traderType: data.traderType || '',
          tradingExperience: data.tradingExperience || '',
          favoriteStocks: data.favoriteStocks || '',
          avatar: data.avatar || getDefaultAvatar()
        });
      } else {
        // Create initial user document if it doesn't exist
        const initialData = {
          displayName: user.displayName || '',
          email: user.email || '',
          bio: '',
          location: '',
          website: '',
          tradingExperience: '',
          favoriteStocks: '',
          avatar: getDefaultAvatar(),
          userRole: getDefaultRole(), // Set default role to 'member'
          createdAt: user.metadata.creationTime,
          lastLogin: user.metadata.lastSignInTime
        };
        await setDoc(doc(db, 'users', user.uid), initialData);
        setUserData(initialData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // Update Firebase Auth profile
      if (editForm.displayName !== user.displayName) {
        await updateProfile(auth.currentUser, {
          displayName: editForm.displayName
        });
      }

      // Update Firestore user document (use setDoc with merge to create if doesn't exist)
      await setDoc(doc(db, 'users', user.uid), {
        displayName: editForm.displayName,
        bio: editForm.bio,
        location: editForm.location,
        traderType: editForm.traderType,
        tradingExperience: editForm.tradingExperience,
        favoriteStocks: editForm.favoriteStocks,
        avatar: editForm.avatar,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Update all user's posts with new avatar
      if (editForm.avatar !== userData?.avatar) {
        const postsQuery = query(collection(db, 'posts'));
        const postsSnapshot = await getDocs(postsQuery);

        const updatePromises = [];
        postsSnapshot.forEach((postDoc) => {
          const postData = postDoc.data();
          if (postData.authorId === user.uid) {
            updatePromises.push(
              updateDoc(doc(db, 'posts', postDoc.id), {
                authorAvatar: editForm.avatar
              })
            );
          }
        });

        if (updatePromises.length > 0) {
          await Promise.all(updatePromises);
        }
      }

      setUserData({
        ...userData,
        ...editForm,
        updatedAt: new Date().toISOString()
      });

      // Notify parent component about avatar update
      if (onAvatarUpdate) {
        onAvatarUpdate(editForm.avatar);
      }

      setIsEditing(false);
      alert('✅ Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('❌ Failed to update profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('❌ Passwords do not match!');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      alert('❌ Password must be at least 6 characters!');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(auth.currentUser, passwordForm.newPassword);
      alert('✅ Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/requires-recent-login') {
        alert('❌ Please log out and log back in before changing your password.');
      } else {
        alert('❌ Failed to change password: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('❌ Please select an image file!');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('❌ File size must be less than 5MB!');
      return;
    }

    // Convert image to base64 data URL and show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditForm({ ...editForm, avatar: reader.result });
      // Keep modal open to show preview - user will close it manually or click another avatar
    };
    reader.readAsDataURL(file);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          {/* Cover Image */}
          <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600"></div>

          {/* Profile Info */}
          <div className="px-6 pb-6">
            {/* Avatar */}
            <div className="flex items-end -mt-16 mb-4">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center text-6xl cursor-pointer hover:opacity-80 transition overflow-hidden"
                     onClick={() => isEditing && setShowAvatarPicker(!showAvatarPicker)}>
                  {(userData?.avatar || editForm.avatar || getDefaultAvatar()).startsWith('data:image') ? (
                    <img
                      src={userData?.avatar || editForm.avatar}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{userData?.avatar || editForm.avatar || getDefaultAvatar()}</span>
                  )}
                </div>
                {isEditing && (
                  <button
                    onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                    className="absolute bottom-0 right-0 bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition">
                    <Camera className="w-4 h-4" />
                  </button>
                )}

              </div>

              <div className="ml-6 flex-1 pb-2">
                {!isEditing ? (
                  <>
                    <h1 className="text-3xl font-bold text-gray-900">
                      {user.displayName || 'Anonymous User'}
                    </h1>
                    {/* Follow Stats */}
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Users className="w-4 h-4" />
                        <span className="text-sm">
                          <span className="font-bold text-gray-900">{followCounts.followers}</span> Followers
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-600">
                        <span className="text-sm">
                          <span className="font-bold text-gray-900">{followCounts.following}</span> Following
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editForm.displayName}
                      onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Display Name"
                    />
                  </div>
                )}
              </div>

              <div className="pb-2">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Profile
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveProfile}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setShowAvatarPicker(false);
                        setEditForm({
                          displayName: user.displayName || '',
                          email: user.email || '',
                          bio: userData?.bio || '',
                          location: userData?.location || '',
                          traderType: userData?.traderType || '',
                          tradingExperience: userData?.tradingExperience || '',
                          favoriteStocks: userData?.favoriteStocks || '',
                          avatar: userData?.avatar || getDefaultAvatar()
                        });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Two Column Layout: Trading Profile and Account Information */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Trading Profile - Left 50% */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Trading Profile</h2>

            <div className="space-y-4">
              {/* Location */}
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., New York, USA"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-purple-500" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium text-gray-900">{userData?.location || 'Not specified'}</p>
                  </div>
                </div>
              )}

              {/* Trader Type */}
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trader Type
                  </label>
                  <select
                    value={editForm.traderType}
                    onChange={(e) => setEditForm({ ...editForm, traderType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select trader type</option>
                    <option value="day-trader">Day Trader</option>
                    <option value="swing-trader">Swing Trader</option>
                    <option value="position-trader">Position Trader</option>
                    <option value="algorithmic-trader">Algorithmic Trader</option>
                    <option value="technical-trader">Technical Trader</option>
                    <option value="dividend-trader">Dividend Trader</option>
                  </select>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Briefcase className="w-5 h-5 text-blue-500" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Trader Type</p>
                    <p className="font-medium text-gray-900 capitalize">{userData?.traderType?.replace('-', ' ') || 'Not specified'}</p>
                  </div>
                </div>
              )}

              {/* Trading Experience */}
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trading Experience
                  </label>
                  <select
                    value={editForm.tradingExperience}
                    onChange={(e) => setEditForm({ ...editForm, tradingExperience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select experience level</option>
                    <option value="beginner">Beginner (0-1 years)</option>
                    <option value="intermediate">Intermediate (1-3 years)</option>
                    <option value="advanced">Advanced (3-5 years)</option>
                    <option value="expert">Expert (5+ years)</option>
                  </select>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Award className="w-5 h-5 text-amber-500" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Trading Experience</p>
                    <p className="font-medium text-gray-900 capitalize">{userData?.tradingExperience || 'Not specified'}</p>
                  </div>
                </div>
              )}

              {/* Favorite Stocks */}
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Favorite Stocks/Watchlist
                  </label>
                  <input
                    type="text"
                    value={editForm.favoriteStocks}
                    onChange={(e) => setEditForm({ ...editForm, favoriteStocks: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., AAPL, TSLA, MSFT, GOOGL"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Favorite Stocks/Watchlist</p>
                    <p className="font-medium text-gray-900">{userData?.favoriteStocks || 'Not specified'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account Information - Right 50% */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Information</h2>

            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-blue-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900">{user.email}</p>
                </div>
                {user.emailVerified ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    Verified
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                    Not Verified
                  </span>
                )}
              </div>

              {/* Account Created */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-green-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Account Created</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(user.metadata.creationTime)}
                  </p>
                </div>
              </div>

              {/* Last Login */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Last Login</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(user.metadata.lastSignInTime)}
                  </p>
                </div>
              </div>

              {/* Sign-in Provider */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-indigo-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Sign-in Method</p>
                  <p className="font-medium text-gray-900">
                    {user.providerData[0]?.providerId === 'google.com' ? 'Google' : 'Email/Password'}
                  </p>
                </div>
              </div>

              {/* User Role */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Shield className="w-5 h-5 text-indigo-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">User Role</p>
                  <div className="flex items-center gap-2">
                    <span className={getRoleBadgeProps(userData?.userRole || USER_ROLES.MEMBER).className}>
                      {getRoleBadgeProps(userData?.userRole || USER_ROLES.MEMBER).label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {ROLE_INFO[userData?.userRole || USER_ROLES.MEMBER]?.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Security Settings</h2>

          {/* Change Password */}
          {user.providerData[0]?.providerId !== 'google.com' && (
            <div>
              {!showPasswordChange ? (
                <button
                  onClick={() => setShowPasswordChange(true)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                >
                  Change Password
                </button>
              ) : (
                <div className="space-y-4">
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="New Password (min 6 characters)"
                  />
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm New Password"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleChangePassword}
                      disabled={loading}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-50"
                    >
                      {loading ? 'Changing...' : 'Update Password'}
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordChange(false);
                        setPasswordForm({ newPassword: '', confirmPassword: '' });
                      }}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {user.providerData[0]?.providerId === 'google.com' && (
            <p className="text-gray-600">
              You're signed in with Google. Password changes must be made through your Google account.
            </p>
          )}
        </div>
      </div>

      {/* Avatar Picker Modal - Fixed Overlay */}
      {showAvatarPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAvatarPicker(false)}>
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Choose Your Avatar</h3>
              <button
                onClick={() => setShowAvatarPicker(false)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Preview Section for Uploaded Image */}
            {editForm.avatar && editForm.avatar.startsWith('data:image') && (
              <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-lg">
                    <img
                      src={editForm.avatar}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-purple-900">Custom Avatar Preview</p>
                    <p className="text-xs text-purple-700">This will be your avatar. Click "Save" to apply it.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowAvatarPicker(false);
                        handleSaveProfile();
                      }}
                      className="px-4 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition font-semibold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditForm({ ...editForm, avatar: getDefaultAvatar() })}
                      className="px-3 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-10 gap-3 max-h-96 overflow-y-auto p-2">
              {AVATARS.slice(0, 49).map((avatar, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setEditForm({ ...editForm, avatar });
                    setShowAvatarPicker(false);
                  }}
                  className={`text-4xl p-3 rounded-lg transition-all duration-200 transform hover:scale-110 hover:shadow-lg flex items-center justify-center ${
                    editForm.avatar === avatar
                      ? 'bg-blue-200 ring-2 ring-blue-500'
                      : 'bg-gray-50 hover:bg-blue-100 hover:ring-2 hover:ring-blue-300'
                  }`}
                  title={`Avatar ${index + 1}`}
                >
                  {avatar}
                </button>
              ))}
              {/* Custom Upload Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`p-3 rounded-lg transition-all duration-200 transform hover:scale-110 hover:shadow-lg flex items-center justify-center overflow-hidden ${
                  editForm.avatar && editForm.avatar.startsWith('data:image')
                    ? 'bg-blue-200 ring-2 ring-blue-500'
                    : 'bg-gradient-to-br from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600'
                } text-white`}
                title="Upload Custom Avatar"
              >
                {editForm.avatar && editForm.avatar.startsWith('data:image') ? (
                  <img
                    src={editForm.avatar}
                    alt="Custom"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Upload className="w-8 h-8" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
